import type {ValidationResult, Widget} from '~~/api/models';
import type {NitroFetchOptions, NitroFetchRequest, TypedInternalResponse} from 'nitropack';
import type {RouterMethod} from 'h3';
import type {ModelStateError} from '~~/api/errorUtils';
import {UserService} from '~/services/sections/UserService';
import {WidgetService} from '~/services/sections/WidgetService';
import {CompanyService} from '~/services/sections/CompanyService';
import {ConfigurationService} from '~/services/sections/ConfigurationService';
import {OioTemplateService} from '~/services/sections/OioTemplateService';
import {NotificationService} from '~/services/sections/NotificationService';
import {AdministrationService} from '~/services/sections/AdministrationService';
import {ItemService} from '~/services/sections/ItemService';
import {FlowService} from '~/services/sections/FlowService';
import {OrganisationService} from '~/services/sections/OrganisationService';
import {InvitationService} from '~/services/sections/InvitationService';
import {FetchError} from 'ofetch';

// Define a custom ValidationError class
export class ValidationError extends Error {
  type = 'ValidationError'; // Add a unique type identifier

  constructor(message: string, public details?: ValidationResult[]) {
    super(message);
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, new.target.prototype); // Restore prototype chain
  }
}

export class ModelStateErrorResponse extends Error {
  type = 'ModelStateError'; // Add a unique type identifier

  constructor(message: string, public details?: ModelStateError[]) {
    super(message);
    this.name = 'ModelStateError';
    Object.setPrototypeOf(this, new.target.prototype); // Restore prototype chain
  }
}

export class ApiService {
  // Nested Service Classes
  public User: UserService;
  public Item: ItemService;
  public OioTemplate: OioTemplateService;
  public Widget: WidgetService;
  public Company: CompanyService;
  public Flow: FlowService;
  public Administration: AdministrationService;
  public Notification: NotificationService;
  public Configuration: ConfigurationService;
  public Organisation: OrganisationService;
  public Invitation: InvitationService;

  public Headers: { cookie?: string } = {};
  public AbortController: AbortController | null = null;

  // Map to keep track of ongoing requests
  private ongoingRequests: Map<string, AbortController> = new Map();

  public activeTenant: string | undefined;
  public tenants: string[] = [];
  public activeWidget: Widget | undefined | null;

  constructor() {
    // Initialize nested services
    this.User = new UserService(this);
    this.Item = new ItemService(this);
    this.Widget = new WidgetService(this);
    this.Company = new CompanyService(this);
    this.Notification = new NotificationService(this);
    this.Administration = new AdministrationService(this);
    this.Flow = new FlowService(this);
    this.Configuration = new ConfigurationService(this);
    this.OioTemplate = new OioTemplateService(this);
    this.Organisation = new OrganisationService(this);
    this.Invitation = new InvitationService(this);
  }

  async get<T>(endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T>> {
    return this.request<T>('get', endpoint, options);
  }

  async post<T>(endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T>> {
    return this.request<T>('post', endpoint, options);
  }

  async delete<T>(endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T>> {
    return this.request<T>('delete', endpoint, options);
  }

  async patch<T>(endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T>> {
    return this.request<T>('patch', endpoint, options);
  }

  async put<T>(endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T>> {
    return this.request<T>('put', endpoint, options);
  }

  private async request<T>(method: RouterMethod, endpoint: string, options: NitroFetchOptions<NitroFetchRequest> = {}): Promise<TypedInternalResponse<NitroFetchRequest, T, typeof method>> {
    const url = this.formatUrl(endpoint);
    // Convert all the query params to a string.
    const queryParams = options.query ? Object.keys(options.query).map(key => `${key}=${options.query?.[key]}`).join('&') : '';
    const requestKey = `${method}:${url}:${queryParams}`; // Unique key per method and URL and query params

    // Abort previous request if it exists
    if (this.ongoingRequests.has(requestKey)) {
      const previousController = this.ongoingRequests.get(requestKey);
      if (previousController) {
        previousController.abort();
      }
      this.ongoingRequests.delete(requestKey);
    }

    // Create a new AbortController for the current request
    let abortController = new AbortController();
    this.ongoingRequests.set(requestKey, abortController);

    // If an AbortController is already set, use it
    if (this.AbortController)
      abortController = this.AbortController;

    const defaultOptions: NitroFetchOptions<NitroFetchRequest> = {
      method,
      ...options,
      headers: {
        ...(options.headers || {}),
        ...this.Headers
      },
      credentials: 'include',
      signal: abortController.signal
    };

    try {
      return await $fetch<T>(url, defaultOptions);
    } catch (error: unknown) {
      if (abortController.signal.aborted) {
        // Handle aborted request if necessary
        console.warn(`Request aborted: ${this.redactSensitiveRequestKey(requestKey)}`);
        throw new Error('Request aborted');
      }
      if (error instanceof FetchError) {
        const response = error.response;

        // Check for validation errors
        if (
          ((response?.status === 400) && (response?.statusText === 'VALIDATION_FAILED')) ||
          ((response?._data?.status === 400 || response?._data?.statusCode === 400)
            && (response?._data?.statusText === 'VALIDATION_FAILED' || response?._data?.statusMessage === 'VALIDATION_FAILED'))
        ) {
          throw new ValidationError('Validation error occurred', response?._data?.data?.validationResults);
        }

        // Check for model state errors
        if (
          (response?.status === 400 && response?.statusText === 'MODEL_STATE_ERROR') ||
          (response?._data?.status === 400 || response?._data?.statusCode === 400) &&
          (response?._data.statusText === 'MODEL_STATE_ERROR' || response?._data?.statusMessage === 'MODEL_STATE_ERROR')
        ) {
          throw new ModelStateErrorResponse('Model state error occurred', response?._data?.data?.modelStateErrors);
        }
      }

      throw error;
    } finally {
      // Clean up the ongoing request
      this.ongoingRequests.delete(requestKey);
    }
  }

  private formatUrl(endpoint: string): string {
    // get nuxt base url
    const baseUrl = process.env.BASE_URL || '';

    return `${baseUrl.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;
  }

  private redactSensitiveRequestKey(requestKey: string): string {
    return requestKey.replace(/\/api\/invitation\/[^:/?#]+/g, '/api/invitation/[redacted]');
  }
  private normalizeHeaderValue(value: string | undefined): string | undefined {
    const normalizedValue = value?.trim();

    if (!normalizedValue || normalizedValue === 'undefined' || normalizedValue === 'null') {
      return undefined;
    }

    return normalizedValue;
  }
  resolveActiveTenant(): string | undefined {
    const resolvedTenant = this.normalizeHeaderValue(this.activeTenant);

    this.activeTenant = resolvedTenant;

    return resolvedTenant;
  }
}